import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { WFPButton } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { formatDateTimeCompact, formatRelativeDateTime } from "@/utils/relative-time";

import { BID_REQUEST_STATUS, type BidRequest } from "../../types";
import {
  getBiddingPeriodLabel,
  getCommodityNames,
  getDaysLeft,
  getFulfilmentChip,
  getStatusChip,
  getSubmittedCount,
  formatBidRequestDate,
} from "../../utils";

interface BidRequestCardProps {
  bidRequest: BidRequest;
  onViewDetails: (id: number) => void;
}

export const BidRequestCard = ({ bidRequest, onViewDetails }: BidRequestCardProps) => {
  const { t } = useTranslation();

  const isOpen = bidRequest.status === BID_REQUEST_STATUS.openToBids;
  const isAccepted = bidRequest.status === BID_REQUEST_STATUS.acceptedBid;
  const showBidsSummary =
    bidRequest.status === BID_REQUEST_STATUS.openToBids ||
    bidRequest.status === BID_REQUEST_STATUS.biddingComplete;
  const daysLeft = getDaysLeft(bidRequest.posting_end_date);
  const statusChip = getStatusChip(bidRequest.status, t);
  const fulfilmentChip = bidRequest.fulfilment_status
    ? getFulfilmentChip(bidRequest.fulfilment_status, t)
    : null;

  return (
    <Card className="gap-0 py-0">
      <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <p className="text-muted-foreground text-xs">
              {t("bidRequests.card.reference", { reference: bidRequest.buyer_reference })}
              {bidRequest.created_datetime && (
                <span title={formatDateTimeCompact(bidRequest.created_datetime)}>
                  {" · "}
                  {formatRelativeDateTime(bidRequest.created_datetime, t)}
                </span>
              )}
            </p>
            <h2 className="font-semibold text-lg">{getCommodityNames(bidRequest)}</h2>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant={statusChip.variant}>{statusChip.label}</Badge>
            {showBidsSummary && (
              <span className="text-muted-foreground">
                {t("bidRequests.card.bidsReceived", {
                  submitted: getSubmittedCount(bidRequest),
                  total: bidRequest.invited_count,
                })}
              </span>
            )}
            {isAccepted && bidRequest.accepted_bid_aggregator_name && (
              <span className="text-muted-foreground">
                {bidRequest.accepted_bid_aggregator_name}
              </span>
            )}
          </div>

          <dl className="flex flex-col gap-1 text-sm">
            <div className="flex gap-2">
              <dt className="font-medium">{t("bidRequests.card.deliveryDate")}</dt>
              <dd className="text-muted-foreground">
                {formatBidRequestDate(bidRequest.delivery_date)}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="font-medium">{t("bidRequests.card.biddingPeriod")}</dt>
              <dd className="text-muted-foreground">
                {getBiddingPeriodLabel(bidRequest)}
                {isOpen && ` (${t("bidRequests.card.daysLeft", { count: daysLeft })})`}
              </dd>
            </div>
            {fulfilmentChip && (
              <div className="flex items-center gap-2">
                <dt className="font-medium">{t("bidRequests.card.fulfilmentLabel")}</dt>
                <dd>
                  <Badge variant={fulfilmentChip.variant}>{fulfilmentChip.label}</Badge>
                </dd>
              </div>
            )}
          </dl>
        </div>

        <div className="shrink-0">
          <WFPButton variant="outline" onClick={() => onViewDetails(bidRequest.id)}>
            {t("bidRequests.card.viewDetails")}
          </WFPButton>
        </div>
      </CardContent>
    </Card>
  );
};
